import { useState, useEffect, useRef, useCallback, memo } from 'react';
import {
    Box, Grid, GridItem, Flex, Heading, Text, Button,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CheckoutForm from './CheckoutForm';
import DispatchOptions from './DispatchOptions';
import PaymentOptions from './PaymentOptions';
import CheckoutOrders from './CheckoutOrders';
import { DashboardSection } from '../components/DashboardStyles';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS
   ───────────────────────────────────────────────────────────── */
const NAVY        = '#1B3B5F';
// const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';
const SUCCESS     = '#10B981';

const FONT      = "'Bricolage Grotesque', sans-serif";
const SHADOW_SM = '0 1px 3px rgba(27,59,95,0.06)';

/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */
const CheckIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.6"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
// const LockIcon = (props) => (
//     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
//         <rect x="3" y="11" width="18" height="11" rx="2"
//             stroke="currentColor" strokeWidth="1.8" />
//         <path d="M7 11V7a5 5 0 0110 0v4"
//             stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
//     </svg>
// );
const EditIcon = (props) => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"
            stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
            stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/* ─────────────────────────────────────────────────────────────
   STEP TOKENS
   ───────────────────────────────────────────────────────────── */
const STEPS = [
    { id: 0, title: 'Customer & address', subtitle: 'Who is this order for?' },
    { id: 1, title: 'Dispatch',           subtitle: 'How should we deliver this order?' },
    { id: 2, title: 'Review & payment',   subtitle: 'Confirm details and pay' },
];

/* ─────────────────────────────────────────────────────────────
   STEP BADGE
   ───────────────────────────────────────────────────────────── */
const StepBadge = memo(function StepBadge({ stepNumber, status }) {
    const isDone   = status === 'done';
    const isActive = status === 'active';
    const bg = isDone ? SUCCESS : isActive ? NAVY : SURFACE_ALT;
    const fg = isDone || isActive ? 'white' : FAINT;
    const border = isDone || isActive ? bg : BORDER;

    return (
        <Flex
            w="32px"
            h="32px"
            borderRadius="full"
            bg={bg}
            color={fg}
            border="2px solid"
            borderColor={border}
            align="center"
            justify="center"
            flexShrink={0}
            transition="all 0.2s ease"
            fontFamily={FONT}
            fontWeight="800"
            fontSize="13px"
        >
            {isDone ? <CheckIcon />  : stepNumber}
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   SECTION SHELL
   ───────────────────────────────────────────────────────────── */
const SectionShell = ({
    stepNumber, title, subtitle, status, isHighlighted, sectionRef, children,
}) => (
    <Box
        ref={sectionRef}
        bg={SURFACE}
        border="1px solid"
        borderColor={status === 'active' ? NAVY : BORDER}
        borderRadius="14px"
        overflow="hidden"
        boxShadow={status === 'active' ? `0 0 0 3px ${CYAN}55, ${SHADOW_SM}` : SHADOW_SM}
        transition="border-color 0.2s ease, box-shadow 0.2s ease"
        sx={{
            '@keyframes highlightPulse': {
                '0%':   { boxShadow: `0 0 0 0 ${CYAN}` },
                '40%':  { boxShadow: `0 0 0 12px ${CYAN}66, ${SHADOW_SM}` },
                '100%': { boxShadow: `0 0 0 0 ${CYAN}00, ${SHADOW_SM}` },
            },
            animation: isHighlighted ? 'highlightPulse 1s ease-out' : 'none',
        }}
    >
        <Flex
            align="flex-start"
            gap="14px"
            p={{ base: '14px 16px', md: '18px 20px' }}
            borderBottom="1px solid"
            borderColor={BORDER}
            bg={status === 'done' ? SURFACE_ALT : SURFACE}
        >
            <StepBadge stepNumber={stepNumber} status={status} />
            <Box flex="1" minW="0">
                <Heading
                    as="h2"
                    fontSize={{ base: '15px', md: '16px' }}
                    fontWeight="800"
                    color={status === 'locked' ? FAINT : INK}
                    fontFamily={FONT}
                    letterSpacing="-0.01em"
                    lineHeight="1.2"
                    mb="2px"
                >
                    {title}
                </Heading>
                <Text
                    fontSize="11.5px"
                    color={status === 'locked' ? FAINT : MUTED}
                    lineHeight="1.4"
                >
                    {subtitle}
                </Text>
            </Box>
        </Flex>
        {children && (
            <Box p={{ base: '14px 16px', md: '20px 24px' }}>
                {children}
            </Box>
        )}
    </Box>
);

/* ─────────────────────────────────────────────────────────────
   COMPLETED SUMMARY
   ───────────────────────────────────────────────────────────── */
const CompletedSummary = ({ children, onEdit }) => (
    <Flex justify="space-between" align="flex-start" gap="14px">
        <Box flex="1" minW="0">
            {children}
        </Box>
        <Button
            size="xs"
            variant="outline"
            borderColor={BORDER}
            color={NAVY}
            fontWeight="700"
            fontSize="11px"
            h="28px"
            borderRadius="7px"
            flexShrink={0}
            leftIcon={<EditIcon />}
            _hover={{ borderColor: NAVY, bg: SURFACE_ALT }}
            onClick={onEdit}
        >
            Edit
        </Button>
    </Flex>
);

/* ─────────────────────────────────────────────────────────────
   LOCKED PLACEHOLDER
   ───────────────────────────────────────────────────────────── */
// const LockedPlaceholder = ({ message }) => (
//     <Box
//         bg={SURFACE_ALT}
//         border="1px dashed"
//         borderColor={BORDER}
//         borderRadius="10px"
//         p="18px"
//         textAlign="center"
//     >
//         <Text fontSize="12px" color={FAINT}>
//             {message}
//         </Text>
//     </Box>
// );

/* ─────────────────────────────────────────────────────────────
   ADDRESS SUMMARY
   ───────────────────────────────────────────────────────────── */
const AddressSummary = ({ address }) => {
    const name = `${address?.first_name || ''} ${address?.last_name || ''}`.trim();
    const addressLine = [address?.line1, address?.city, address?.state, address?.country]
        .filter(Boolean).join(', ');

    return (
        <Box>
            <Text fontSize="13px" fontWeight="700" color={INK} fontFamily={FONT} mb="2px">
                {name || 'Customer'}
            </Text>
            {addressLine && (
                <Text fontSize="12px" color={MUTED} lineHeight="1.4" noOfLines={2}>
                    {addressLine}
                </Text>
            )}
            {address?.phone && (
                <Text fontSize="11.5px" color={MUTED} mt="2px">
                    {address.phone}
                </Text>
            )}
            {address?.email && (
                <Text fontSize="11.5px" color={MUTED}>
                    {address.email}
                </Text>
            )}
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────
   DISPATCH SUMMARY
   ───────────────────────────────────────────────────────────── */
const DispatchSummary = ({ dispatcher, dispatchType }) => {
    const name = dispatcher?.name ?? dispatcher?.carrier_name ?? 'Delivery';
    const rate = dispatcher?.carrier_rate_description ?? 'Pickup from store';
    const cost = dispatcher?.amount;

    return (
        <Box>
            <Flex align="center" gap="10px" mb="2px" flexWrap="wrap">
                <Text fontSize="13px" fontWeight="700" color={INK} fontFamily={FONT}>
                    {name}
                </Text>
                {cost != null && (
                    <Text fontSize="13px" fontWeight="800" color={NAVY} fontFamily={FONT}>
                        ₦{Number(cost).toLocaleString()}
                    </Text>
                )}
            </Flex>
            <Text fontSize="12px" color={MUTED} textTransform="capitalize" lineHeight="1.4">
                {rate}
            </Text>
            {dispatchType && (
                <Text fontSize="10.5px" color={FAINT} mt="2px" fontWeight="700" letterSpacing="0.04em">
                    {dispatchType.toUpperCase()}
                </Text>
            )}
        </Box>
    );
};

/* ─────────────────────────────────────────────────────────────
   MAIN
   ───────────────────────────────────────────────────────────── */
const Checkout = () => {
    const navigate = useNavigate();
    const productReducer = useSelector((state) => state.productReducer);
    const { cartItems, quoteId } = productReducer;

    const totalAmount = cartItems?.items?.reduce(
        (a, b) => a + Number(b.price * b.qty),
        0
    );

    const [activeStep, setActiveStep]           = useState(0);
    const [highlightedStep, setHighlightedStep] = useState(null);
    const [quote, setQuote]                     = useState(0);
    const [isLoadingQuote, setIsLoadingQuote]   = useState(false);

    const [checkoutPayload, setCheckoutPayload] = useState({
        address: null,
        dispatcher: null,
        payment: null,
        cartItems: cartItems,
    });

    const section0Ref = useRef(null);
    const section1Ref = useRef(null);
    const section2Ref = useRef(null);
    const sectionRefs = [section0Ref, section1Ref, section2Ref];

    useEffect(() => {
        setCheckoutPayload((prev) => ({
            ...prev,
            cartItems: cartItems,
            dispatcher: null,
        }));
        setActiveStep(0);
        setQuote(0);
    }, [cartItems]);

    const fetchQuote = useCallback((value) => {
        setQuote(value);
        setIsLoadingQuote(false);
    }, []);

    const handleStepChange = useCallback((nextStep) => {
        if (nextStep === activeStep) return;
        setActiveStep(nextStep);

        setTimeout(() => {
            setHighlightedStep(nextStep);
            sectionRefs[nextStep]?.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        }, 50);

        setTimeout(() => {
            setHighlightedStep(null);
        }, 1100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStep]);

    const getStepStatus = (stepId) => {
        if (stepId === activeStep) return 'active';
        if (stepId < activeStep) return 'done';
        return 'locked';
    };

    const editStep = (stepId) => handleStepChange(stepId);

    const handlePaymentSuccess = () => {
        setActiveStep(0);
    };

    const renderStepBody = (stepId, status) => {
       

        if (status === 'done') {
            if (stepId === 0) {
                return (
                    <CompletedSummary onEdit={() => editStep(0)}>
                        <AddressSummary address={checkoutPayload?.address} />
                    </CompletedSummary>
                );
            }
            if (stepId === 1) {
                return (
                    <CompletedSummary onEdit={() => editStep(1)}>
                        <DispatchSummary
                            dispatcher={checkoutPayload?.dispatcher}
                            dispatchType={checkoutPayload?.dispatchType}
                        />
                    </CompletedSummary>
                );
            }
            return null;
        }

        if (stepId === 0) {
            return (
                <CheckoutForm
                    setTabIndex={handleStepChange}
                    setCheckoutPayload={setCheckoutPayload}
                    checkoutPayload={checkoutPayload}
                />
            );
        }
        if (stepId === 1) {
            return (
                <DispatchOptions
                    setTabIndex={handleStepChange}
                    checkoutPayload={checkoutPayload}
                    setCheckoutPayload={setCheckoutPayload}
                    fetchQuote={fetchQuote}
                />
            );
        }
        if (stepId === 2) {
            return (
                <PaymentOptions
                    setTabIndex={handleStepChange}
                    checkoutPayload={checkoutPayload}
                    setCheckoutPayload={setCheckoutPayload}
                    quote={quote}
                    onPaymentSuccess={handlePaymentSuccess}
                />
            );
        }
        return null;
    };

    return (
        <DashboardSection>
            <Box
                maxW="1320px"
                mx="auto"
                w="full"
                px={{ base: '12px', md: '0' }}
                fontFamily={FONT}
            >
               {/* PAGE HEADER */}
<Box mb={{ base: '20px', md: '28px' }}>
    <Flex align="center" gap="10px" mb="4px">
        <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            color={MUTED}
            h="36px"
            w="36px"
            minW="36px"
            p="0"
            borderRadius="8px"
            _hover={{ bg: SURFACE_ALT, color: NAVY }}
            _focusVisible={{
                outline: `2px solid ${NAVY}`,
                outlineOffset: '2px',
            }}
            aria-label="Go back"
        >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M19 12H5M12 19l-7-7 7-7"
                    stroke="currentColor" strokeWidth="2.2"
                    strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </Button>
        <Heading
            as="h1"
            fontSize={{ base: '22px', md: '26px' }}
            fontWeight="800"
            color={INK}
            fontFamily={FONT}
            letterSpacing="-0.02em"
            lineHeight="1.1"
        >
            Checkout
        </Heading>
    </Flex>
    <Text fontSize="12px" color={MUTED} ml={{ base: '46px', md: '46px' }}>
        Complete your order in 3 steps
    </Text>
</Box>

                {/* BODY GRID — sections on left, summary on right */}
                <Grid
                    templateColumns={{ base: '1fr', lg: '1fr 520px' }}
                    gap={{ base: '20px', lg: '32px' }}
                    alignItems="start"
                >
                    {/* LEFT — STACKED SECTIONS */}
                    <GridItem minW="0">
                        <Flex direction="column" gap="14px">
                            {STEPS.map((step) => {
                                const status = getStepStatus(step.id);
                                return (
                                    <SectionShell
                                        key={step.id}
                                        stepNumber={step.id + 1}
                                        title={step.title}
                                        subtitle={step.subtitle}
                                        status={status}
                                        isHighlighted={highlightedStep === step.id}
                                        sectionRef={sectionRefs[step.id]}
                                    >
                                        {renderStepBody(step.id, status)}
                                    </SectionShell>
                                );
                            })}
                        </Flex>
                    </GridItem>

                    {/* RIGHT — ORDER SUMMARY (sticky on desktop, scrolls internally if tall) */}
                    <GridItem minW="0">
                        <Box
                            position={{ base: 'static', lg: 'sticky' }}
                            top={{ lg: '24px' }}
                            alignSelf="start"
                            maxH={{ lg: 'calc(100vh - 48px)' }}
                            overflowY={{ lg: 'auto' }}
                            css={{
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#E8EDF5 transparent',
                                '&::-webkit-scrollbar': {
                                    width: '6px',
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: 'transparent',
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: '#E8EDF5',
                                    borderRadius: '3px',
                                },
                                '&::-webkit-scrollbar-thumb:hover': {
                                    background: '#A8B5CC',
                                },
                            }}
                        >
                            <CheckoutOrders
                                cartItems={cartItems}
                                totalAmount={totalAmount}
                                quoteId={quoteId}
                                quote={quote}
                                loading={isLoadingQuote}
                            />
                        </Box>
                    </GridItem>
                </Grid>
            </Box>
        </DashboardSection>
    );
};

export default Checkout;